'use client';

import { type FC, useEffect, useRef, useState } from 'react';
import { cn } from '../../../utils/cn';
import { Button, BottomSheet } from '../../ui';
import { formatCreditsNumber } from '../../../utils/formatters';
import { FaShieldAlt, FaCheckCircle, FaPlus, FaRegQuestionCircle } from '../../icons';
import { WEAPONS } from '@cs-shot/battle-sim';
import type { ArmorType, Loadout, WeaponClass } from '@cs-shot/battle-sim';
import type { Ally } from './useRushMidMatch';
import { useLocalTranslation } from '../../../hooks/useLocalTranslation';
import { ScreenContent, ScreenFixedBottom } from '../../../screen';
import { SideIcon } from '../SideIcon';
import { WEAPON_ICON_SRC, GRENADE_ICON_SRC, SMOKE_ICON_SRC, FLASH_ICON_SRC, weaponKeyOf } from '../spriteLayout';
import { RosterRow } from './shared/RosterRow';
import { AtlasIcon } from './shared/AtlasIcon';
import { GearInfoSheet } from './GearInfoSheet';
import { playSfx } from './sfx';
import translations from './RushMid.translations';

const BuyDollars: FC<{ amount: number; className?: string }> = ({ amount, className }) => (
  <span className={cn('inline-flex items-center gap-0.5', className)}>
    <span className="font-bold text-accent-green">$</span>
    {formatCreditsNumber(amount)}
  </span>
);
import {
  WEAPON_ORDER, WEAPON_COST,
  ARMOR_ORDER, ARMOR, GRENADE_COST, SMOKE_COST, MEDKIT_COST, FLASH_COST,
  emptyLoadout, remaining, isValid,
  armorCost, canSetWeapon, canSetArmor, canTakeGrenade, canTakeSmoke, canTakeMedkit, canTakeFlash,
} from './loadout';
import { clockOffset, saveDelay, secondsLeft } from './buyClock';

// WHY: окно закупа зеркалит бэкендовый BUY_TIMEOUT_MS
const BUY_WINDOW_S = 20;

// WHY: длина раунда в тиках = (4..7 сегментов, среднее 5.5) × 28 из battle-sim/round.ts — MIN/MAX_SEGMENTS и SEG_TICKS не
// экспортируются
const AVG_ROUND_TICKS = 154;
const FASTEST_FIRE_TICKS = Math.min(...Object.values(WEAPONS).map((w) => w.fireTicks));

const WEAPON_LABEL_KEY: Record<WeaponClass, 'weaponAwp' | 'weaponRifle' | 'weaponLmg'> = {
  awp: 'weaponAwp',
  rifle: 'weaponRifle',
  lmg: 'weaponLmg',
};
const WEAPON_BLURB_KEY: Record<WeaponClass, 'weaponAwpBlurb' | 'weaponRifleBlurb' | 'weaponLmgBlurb'> = {
  awp: 'weaponAwpBlurb',
  rifle: 'weaponRifleBlurb',
  lmg: 'weaponLmgBlurb',
};
const ARMOR_LABEL_KEY: Record<ArmorType, 'armorNone' | 'armorLight' | 'armorHeavy'> = {
  none: 'armorNone',
  light: 'armorLight',
  heavy: 'armorHeavy',
};
const ARMOR_BLURB_KEY: Record<ArmorType, 'armorNoneBlurb' | 'armorLightBlurb' | 'armorHeavyBlurb'> = {
  none: 'armorNoneBlurb',
  light: 'armorLightBlurb',
  heavy: 'armorHeavyBlurb',
};

const weaponImg = (side: 't' | 'ct', w: WeaponClass) => WEAPON_ICON_SRC[weaponKeyOf(side, w)];

export interface BuyScreenProps {
  side: 't' | 'ct';
  teammates?: Ally[];
  enemies?: Ally[];
  buyDeadline?: number;
  serverNow?: number;
  savedLoadout?: Loadout | null;
  savedReady?: boolean;
  onReady: (loadout: Loadout) => void;
  onSave?: (loadout: Loadout) => void;
}

type Sheet = 'weapon' | 'armor' | null;

const SAVE_DEBOUNCE_MS = 300;

export const BuyScreen: FC<BuyScreenProps> = ({ side, teammates = [], enemies = [], buyDeadline, serverNow, savedLoadout, savedReady = false, onReady, onSave }) => {
  const { t } = useLocalTranslation(translations);
  const [l, setL] = useState<Loadout>(() => savedLoadout ?? emptyLoadout());
  const [sheet, setSheet] = useState<Sheet>(null);
  const [gearInfo, setGearInfo] = useState(false);
  const left = remaining(l);

  // WHY: buyDeadline — серверное абсолютное время; часы устройства (прод-кейс N1see, +1 мин) не должны решать, закрыт ли
  // закуп
  const offsetRef = useRef(0);
  offsetRef.current = clockOffset(serverNow, Date.now());

  // WHY: на открытии не сохранять — повторная отправка стартового кита при ремаунте стирала выбор игрока на сервере
  const [dirty, setDirty] = useState(false);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  useEffect(() => {
    if (!dirty) return;
    if (!isValid(l)) return;
    const id = window.setTimeout(() => {
      onSaveRef.current?.(l);
    }, saveDelay(buyDeadline, Date.now(), offsetRef.current, SAVE_DEBOUNCE_MS));
    return () => window.clearTimeout(id);
  }, [l, dirty, buyDeadline]);

  const [submitted, setSubmitted] = useState(savedReady);
  const submittedRef = useRef(savedReady);

  const pick = (next: Loadout) => { if (submittedRef.current) return; setDirty(true); setL(next); };

  const pickWeapon = (w: WeaponClass) => { if (canSetWeapon(l, w)) { pick({ ...l, weapon: w }); setSheet(null); } };
  const pickArmor = (a: ArmorType) => { if (canSetArmor(l, a)) { pick({ ...l, armor: a }); setSheet(null); } };
  const toggleNade = () => { const next = { ...l, grenade: !l.grenade }; if (!next.grenade || isValid(next)) pick(next); };
  const toggleSmoke = () => { const next = { ...l, smoke: !l.smoke }; if (!next.smoke || isValid(next)) pick(next); };
  const toggleFlash = () => { const next = { ...l, flash: !l.flash }; if (!next.flash || isValid(next)) pick(next); };
  const toggleMedkit = () => { const next = { ...l, medkit: !l.medkit }; if (!next.medkit || isValid(next)) pick(next); };
  const openSheet = (s: Sheet) => { if (submittedRef.current) return; setSheet(s); };

  const lock = () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitted(true);
    setSheet(null);
  };
  const expire = () => {
    if (submittedRef.current) return;
    if (dirty && isValid(l)) onSaveRef.current?.(l);
    lock();
  };

  const confirm = () => {
    if (submittedRef.current) return;
    lock();
    onReady(l);
  };

  return (
    <>
    <ScreenContent hideLegalSection scrollKey="rush-mid-buy">
    <div data-sound="ring" className="flex flex-col px-4 pt-2">

      <div className="flex items-center gap-3">
        <SideIcon side={side} size={72} />
        <div className="min-w-0">
          <div className="text-2xl font-bold text-label-primary">{t(side === 'ct' ? 'sideCt' : 'sideT')}</div>
          <div className="mt-0.5 inline-flex items-center gap-1 text-sm text-label-secondary">
            {t('remaining')}
            <span className={cn('font-bold', left < 0 ? 'text-accent-red' : 'text-label-primary')}>
              <BuyDollars amount={left} />
            </span>
          </div>
        </div>
      </div>


      <div className="mt-4 flex items-center justify-between">
        <div className="text-sm text-label-secondary">{t('loadout')}</div>
        <button onClick={() => setGearInfo(true)} className="flex items-center gap-1 text-sm text-accent-blue">
          <FaRegQuestionCircle size={14} />{t('gearInfoOpen')}
        </button>
      </div>

      <div className={cn('mt-2 grid grid-cols-2 gap-2', submitted && 'pointer-events-none opacity-50')}>
        <Slot label={t('weapon')} onClick={() => openSheet('weapon')}
          media={<AtlasIcon src={weaponImg(side, l.weapon)} size={44} />}
          value={t(WEAPON_LABEL_KEY[l.weapon])} cost={WEAPON_COST[l.weapon]} />
        <Slot label={t('armor')} onClick={() => openSheet('armor')}
          media={<FaShieldAlt className={l.armor === 'none' ? 'text-white/30' : 'text-accent-blue'} size={24} />}
          value={t(ARMOR_LABEL_KEY[l.armor])} cost={armorCost(l.armor)} />
        <Slot label={t('grenade')} onClick={toggleNade} active={l.grenade}
          media={<AtlasIcon src={GRENADE_ICON_SRC} size={28} className={cn(!l.grenade && 'opacity-40 grayscale')} />}
          value={l.grenade ? t('grenadeTaken') : t('grenadeNone')} cost={l.grenade ? GRENADE_COST : 0}
          badge={l.grenade ? <FaCheckCircle className="text-accent-green" size={13} /> : (canTakeGrenade(l) ? <FaPlus className="text-label-secondary" size={11} /> : null)} />

        {teammates.length > 0 && (
        <Slot label={t('smoke')} onClick={toggleSmoke} active={l.smoke}
          media={<AtlasIcon src={SMOKE_ICON_SRC} size={28} className={cn(!l.smoke && 'opacity-40 grayscale')} />}
          value={l.smoke ? t('grenadeTaken') : t('grenadeNone')} cost={l.smoke ? SMOKE_COST : 0}
          badge={l.smoke ? <FaCheckCircle className="text-accent-green" size={13} /> : (canTakeSmoke(l) ? <FaPlus className="text-label-secondary" size={11} /> : null)} />
        )}

        {teammates.length > 0 && (
        <Slot label={t('flash')} onClick={toggleFlash} active={l.flash}
          media={<AtlasIcon src={FLASH_ICON_SRC} size={28} className={cn(!l.flash && 'opacity-40 grayscale')} />}
          value={l.flash ? t('grenadeTaken') : t('grenadeNone')} cost={l.flash ? FLASH_COST : 0}
          badge={l.flash ? <FaCheckCircle className="text-accent-green" size={13} /> : (canTakeFlash(l) ? <FaPlus className="text-label-secondary" size={11} /> : null)} />
        )}
        <Slot label={t('medkit')} onClick={toggleMedkit} active={l.medkit}
          media={<div className={cn('flex h-7 w-7 items-center justify-center rounded-md', l.medkit ? 'bg-accent-green/25' : 'bg-white/5')}><FaPlus className={l.medkit ? 'text-accent-green' : 'text-white/30'} size={16} /></div>}
          value={l.medkit ? t('grenadeTaken') : t('grenadeNone')} cost={l.medkit ? MEDKIT_COST : 0}
          badge={l.medkit ? <FaCheckCircle className="text-accent-green" size={13} /> : (canTakeMedkit(l) ? <FaPlus className="text-label-secondary" size={11} /> : null)} />
      </div>


      {teammates.length > 0 && (
        <div className="mt-4">
          <div className="text-sm text-label-secondary">{t('allies')}</div>
          <div className="mt-2 flex flex-col gap-2">
            {teammates.map((tm) => (
              <RosterRow key={tm.index} side={side} member={tm} fallbackKeyPrefix="rushmid-ally" showLoadout />
            ))}
          </div>
        </div>
      )}


      {enemies.length > 0 && (
        <div className="mt-4">
          <div className="text-sm text-label-secondary">{t('enemies')}</div>
          <div className="mt-2 flex flex-col gap-2">
            {enemies.map((en) => (
              <RosterRow key={en.index} side={side} member={en} fallbackKeyPrefix="rushmid-enemy" />
            ))}
          </div>
        </div>
      )}


      <BottomSheet isOpen={sheet === 'weapon'} onClose={() => setSheet(null)} title={t('weapon')} heightClass="h-auto max-h-[80vh]">
        <div data-sound="ring" className="space-y-2 px-4 pb-4">
          {WEAPON_ORDER.map((w) => {
            const s = WEAPONS[w];
            const can = canSetWeapon(l, w);
            return (
              <button key={w} disabled={!can} onClick={() => pickWeapon(w)}
                className={cn('w-full rounded-2xl border p-3 text-start transition-colors',
                  l.weapon === w ? 'border-accent-blue bg-accent-blue/15' : 'border-white/10 bg-bg-secondary',
                  !can && 'opacity-40')}>
                <div className="flex items-center gap-3">
                  <AtlasIcon src={weaponImg(side, w)} size={64} className="shrink-0" />
                  <span className="text-base font-semibold text-label-primary">{t(WEAPON_LABEL_KEY[w])}</span>
                  <BuyDollars amount={WEAPON_COST[w]} className="ml-auto text-label-secondary" />
                </div>
                <p className="mt-1 text-xs text-label-secondary">{t(WEAPON_BLURB_KEY[w])}</p>
                <div className="mt-2 flex gap-2.5">
                  <Stat label={t('statDamage')} v={s.damage / 100} text={`${s.damage}`} />
                  <Stat label={t('statRate')} v={Math.min(1, FASTEST_FIRE_TICKS / s.fireTicks)} text={`×${Math.max(1, Math.floor(AVG_ROUND_TICKS / s.fireTicks))}`} />
                  <Stat label={t('statAccuracy')} v={s.accuracy} text={`${Math.round(s.accuracy * 100)}%`} />
                  <Stat label={t('statCrit')} v={s.critChance / 0.35} text={`${Math.round(s.critChance * 100)}%`} />
                </div>
              </button>
            );
          })}
        </div>
      </BottomSheet>


      <BottomSheet isOpen={sheet === 'armor'} onClose={() => setSheet(null)} title={t('armor')} heightClass="h-auto max-h-[70vh]">
        <div data-sound="ring" className="space-y-2 px-4 pb-4">
          {ARMOR_ORDER.map((a) => {
            const can = canSetArmor(l, a);
            return (
              <button key={a} disabled={!can} onClick={() => pickArmor(a)}
                className={cn('flex w-full items-center gap-3 rounded-2xl border p-3 text-start transition-colors',
                  l.armor === a ? 'border-accent-blue bg-accent-blue/15' : 'border-white/10 bg-bg-secondary',
                  !can && 'opacity-40')}>
                <FaShieldAlt className={a === 'none' ? 'text-white/30' : 'text-accent-blue'} size={26} />
                <div className="min-w-0">
                  <div className="text-base font-semibold text-label-primary">{t(ARMOR_LABEL_KEY[a])}</div>
                  <div className="text-xs text-label-secondary">{t(ARMOR_BLURB_KEY[a])}</div>
                  {a !== 'none' && (
                    <div className="mt-0.5 text-xs text-label-secondary">{t('armorNumbers', { points: ARMOR[a].points })}</div>
                  )}
                </div>
                <BuyDollars amount={armorCost(a)} className="ml-auto shrink-0 text-label-secondary" />
              </button>
            );
          })}
        </div>
      </BottomSheet>

      <GearInfoSheet isOpen={gearInfo} onClose={() => setGearInfo(false)} />
    </div>
    </ScreenContent>
    <ScreenFixedBottom className="px-4">
      <CountdownButton valid={isValid(l)} submitted={submitted} deadline={buyDeadline}
        offset={offsetRef.current} offsetKnown={serverNow !== undefined} onConfirm={confirm} onExpire={expire} />
    </ScreenFixedBottom>
    </>
  );
};

const CountdownButton: FC<{
  valid: boolean; submitted: boolean; deadline?: number;
  offset: number;
  offsetKnown: boolean;
  onConfirm: () => void; onExpire: () => void;
}> = ({ valid, submitted, deadline, offset, offsetKnown, onConfirm, onExpire }) => {
  const { t } = useLocalTranslation(translations);
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const end = deadline ?? Date.now() + offset + BUY_WINDOW_S * 1000;
  const [remain, setRemain] = useState(secondsLeft(end, Date.now(), offset));
  const firedRef = useRef(false);
  const sawOpenRef = useRef(offsetKnown || remain > 0);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const beepedRef = useRef(99);
  useEffect(() => {
    const h = window.setInterval(() => {
      const s = secondsLeft(end, Date.now(), offsetRef.current);
      setRemain(s);
      if (s > 0) sawOpenRef.current = true;
      if (s <= 3 && s >= 1 && s !== beepedRef.current) {
        beepedRef.current = s;
        playSfx('timer_beep', 0.5);
      }
      if (s <= 0 && sawOpenRef.current && !firedRef.current) { firedRef.current = true; onExpireRef.current(); }
    }, 250);
    return () => window.clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-once countdown; `end` captured intentionally at mount
  }, []);
  return (
    <Button variant="primary" size="l" stretched data-sound="ring" disabled={!valid || submitted} onClick={() => onConfirm()}>
      {submitted ? t('waiting') : t('toBattleIn', { sec: remain })}
    </Button>
  );
};

const Slot: FC<{
  label: string; value: string; cost: number; media: React.ReactNode;
  onClick: () => void; active?: boolean; badge?: React.ReactNode;
}> = ({ label, value, cost, media, onClick, active, badge }) => (
  <button onClick={onClick}
    className={cn('relative flex items-center gap-2 rounded-xl border p-2 text-start transition-colors',
      active ? 'border-accent-blue bg-accent-blue/15' : 'border-white/10 bg-bg-secondary')}>
    <div className="flex h-9 w-11 shrink-0 items-center justify-center">{media}</div>
    <div className="min-w-0 flex-1">
      <div className="truncate text-[10px] uppercase tracking-wide text-label-secondary">{label}</div>
      <div className="truncate text-xs font-semibold text-label-primary">{value}</div>
    </div>
    <div className="flex shrink-0 items-center gap-1">
      <BuyDollars amount={cost} className="text-label-secondary" />
      {badge}
    </div>
  </button>
);

const Stat: FC<{ label: string; v: number; text: string }> = ({ label, v, text }) => (
  <div className="flex-1">
    <div className="flex items-baseline justify-between gap-1">
      <div className="truncate text-[10px] uppercase tracking-wide text-label-secondary">{label}</div>
      <div className="text-[10px] font-semibold tabular-nums text-label-primary">{text}</div>
    </div>
    <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-white/10">
      <div className="h-full rounded-full bg-side-t" style={{ width: `${Math.max(6, Math.min(100, v * 100))}%` }} />
    </div>
  </div>
);
